/**
 * SeriesPreview — the dates a recurring schedule will create, listed under the
 * cadence controls. Each date and start time can be changed by hand before
 * anything is created (a holiday moved a day, an earlier slot one week). A
 * series that cannot be created as stated shows why instead of a list.
 */
import { SeriesOccurrenceRow } from './SeriesOccurrenceRow';
import type { SeriesEdit, SeriesPreview as Preview } from './workshopCreateSeries';

const BOX: React.CSSProperties = {
  border: '1px solid var(--nb-border)',
  borderRadius: 10,
  padding: '10px 12px',
  fontSize: 11.5,
};

const HINT: React.CSSProperties = { fontSize: 10.5, color: 'var(--nb-subtle)', lineHeight: 1.5 };

export function SeriesPreview({
  preview,
  minDate,
  error,
  onEdit,
  onReset,
}: {
  preview: Preview | null;
  minDate: string;
  /** The step's series-dates error, shown under the list. */
  error?: string;
  onEdit: (index: number, field: keyof SeriesEdit, value: string) => void;
  onReset: (index: number) => void;
}) {
  if (!preview) {
    return (
      <div style={HINT}>
        Fill in the first date, start time and how the series ends to see the dates.
      </div>
    );
  }
  if (preview.problem) {
    return (
      <div role="alert" style={{ ...BOX, color: '#c0392b', borderColor: '#c0392b' }}>
        {preview.problem}
      </div>
    );
  }
  return (
    <div style={BOX} data-testid="series-preview">
      <div style={{ fontWeight: 700, color: 'var(--nb-ink)' }}>
        {preview.label} · {preview.count} workshops
      </div>
      <div style={{ ...HINT, margin: '2px 0 8px' }}>
        Change any date or start time below. Each workshop keeps the same length.
        {preview.editedCount > 0 && ' Changing the schedule above resets your changes.'}
      </div>
      <ol
        aria-label="Series dates"
        style={{
          margin: 0,
          padding: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {preview.occurrences.map((o) => (
          <SeriesOccurrenceRow
            key={o.index}
            occurrence={o}
            minDate={minDate}
            onEdit={onEdit}
            onReset={onReset}
          />
        ))}
      </ol>
      {error && (
        <div style={{ fontSize: 10.5, color: 'var(--nb-risk)', marginTop: 8 }}>{error}</div>
      )}
    </div>
  );
}
